import { t } from 'i18next';
import { MedicalEventData, MedicalEventType } from 'models/patientMedicalEvent';
import { formatDateHighcharts } from 'utils/dates';
import { showBlankingPeriodLabel } from 'utils/logs';
import { chartTooltip } from 'components/Charts/ChartOptions/ChartTooltip';
import { HourFormat, SymptomSeverities } from 'models/generic';

export const convertSeveritytoChartValue = (severity?: string) => {
  switch (severity) {
    case '0':
      return 0;
    case '1':
      return 1;
    case '2a':
      return 2;
    case '2b':
      return 3;
    case '3':
      return 4;
    case '4':
      return 5;
    default:
      return 0;
  }
};

export const convertSeverityToExtendedValue = (severity?: string) => {
  if (!severity) return '-';
  if (!Object.values(SymptomSeverities).includes(severity)) return '-';
  return `${severity} - ${t(`symptom_severity_${severity}`)}`;
};

export const convertChartValueToSeverity = (chartValue: number) => {
  switch (chartValue) {
    case 0:
    case 1:
      return t('symptom_severity_1');
    case 2:
      return t('symptom_severity_2a');
    case 3:
      return t('symptom_severity_2b');
    case 4:
      return t('symptom_severity_3');
    case 5:
      return t('symptom_severity_4');
    default:
      return t('symptom_severity_1');
  }
};

export const logTooltipContent = (hourFormat: HourFormat, log?: MedicalEventData) => {
  if (!log) return '';

  const logFields = [
    {
      label: t('name_label'),
      value:
        log.type === MedicalEventType.Treatment && log.treatment?.method
          ? t(`add_log_form_${log.treatment.method}`)
          : undefined,
      useAddLogFormTranslationValue: true,
    },
    {
      label: t('name_label'),
      value:
        log.type === MedicalEventType.Treatment && log.diagnosis?.method
          ? t(`add_log_log_type_diagnostic-result`)
          : undefined,
      useAddLogFormTranslationValue: true,
    },
    { label: t('add_log_procedure_type'), value: log.treatment?.procedure?.type },
    { label: t('add_log_iteration'), value: log.treatment?.iteration },
    { label: t('add_log_technology'), value: log.treatment?.procedure?.technology },
    { label: t('add_log_vendor'), value: log.treatment?.procedure?.vendor },
    {
      label: t('add_log_blanking_period'),
      value: log.treatment?.procedure?.blankingPeriodInWeeks
        ? showBlankingPeriodLabel(log.treatment?.procedure?.blankingPeriodInWeeks)
        : undefined,
      useAddLogFormTranslationValue: true,
    },
    { label: t('add_log_medication_type'), value: log.treatment?.medication?.type },
    { label: t('add_log_medication_protocol'), value: log.treatment?.medication?.protocol },
    { label: t('add_log_method'), value: log.diagnosis?.method },
    { label: t('add_log_result'), value: log.diagnosis?.result },
    { label: t('note_label'), value: log.comment, useAddLogFormTranslationValue: true },
  ];

  const tooltipData = Object.values(logFields)
    .filter((logField) => logField.value)
    .map((logField) => {
      if (logField.useAddLogFormTranslationValue) return { label: logField.label, value: logField.value };
      return { label: logField.label, value: t(`add_log_form_${logField.value}`) };
    });

  return chartTooltip({
    date: formatDateHighcharts(log.loggedTimestamp, hourFormat, true),
    tooltipData,
  });
};
